import { isDesktop } from '../../lib/platform.ts'
import type { Atividade, Plataforma } from '@orbind/core'

/**
 * Atividade da pessoa no COMPUTADOR, não só na janela do Orbind
 * (SPEC-20260924-0924).
 *
 * O Orbind desktop vive na bandeja. Quem passa o dia em outro programa não
 * gera mouse nem teclado dentro da janela dele e aparecia "ausente" para os
 * colegas, mesmo trabalhando sem parar.
 *
 * Só o shell nativo responde isso. O navegador não expõe atividade fora da
 * aba — por segurança, e com razão: seria um sensor de vigilância embutido em
 * toda página. No navegador, o Orbind continua vendo só a própria janela.
 *
 * A chamada vai por `__TAURI_INTERNALS__`, sem `@tauri-apps/api`: o mesmo
 * bundle roda no navegador (mesmo padrão de notificacao-nativa.ts).
 */

type Invoke = (comando: string, argumentos?: Record<string, unknown>) => Promise<unknown>

function invokeDoDesktop(): Invoke | null {
  if (!isDesktop()) return null
  const w = window as unknown as { __TAURI_INTERNALS__?: { invoke?: Invoke } }
  return w.__TAURI_INTERNALS__?.invoke ?? null
}

/** Onde esta sessão está rodando — a lista mostra um ícone por plataforma. */
export function plataformaDaSessao(): Plataforma {
  if (isDesktop()) return 'desktop'
  // celular e tablet entram como `mobile`; o resto é navegador de computador
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent
  return /Android|iPhone|iPad|iPod|Mobile/i.test(ua) ? 'mobile' : 'navegador'
}

/**
 * Segundos desde a última entrada do usuário no computador.
 *
 * `null` quando não dá para saber: navegador, Wayland, ou qualquer sistema em
 * que o shell não sabe responder. Quem chama trata isso como "não sei", nunca
 * como "está parado".
 */
export async function segundosSemAtividadeNoSistema(): Promise<number | null> {
  const invoke = invokeDoDesktop()
  if (!invoke) return null
  try {
    const resposta = await invoke('segundos_sem_atividade')
    return typeof resposta === 'number' && Number.isFinite(resposta) && resposta >= 0 ? resposta : null
  } catch {
    // shell antigo, sem este comando: o desktop segue como o navegador
    return null
  }
}

/**
 * O PROGRAMA em primeiro plano no computador — só o nome, "Microsoft Excel"
 * (SPEC-20261020-2341-programa-em-uso-na-presenca). `null` fora do desktop, em shell antigo
 * sem o comando, ou quando o sistema não sabe dizer.
 */
export async function programaEmUsoNoSistema(): Promise<string | null> {
  const invoke = invokeDoDesktop()
  if (!invoke) return null
  try {
    const resposta = await invoke('programa_em_uso')
    return typeof resposta === 'string' && resposta.trim() !== '' ? resposta.trim() : null
  } catch {
    return null
  }
}

/**
 * O que reportar, juntando a janela e o computador.
 *
 * A janela manda quando está ativa — é a presença mais forte que existe. Com a
 * janela parada, o sistema decide entre "no computador" e "parado", usando o
 * MESMO tempo configurado no Console: duas réguas diferentes fariam a pessoa
 * saltar de estado sem motivo visível.
 */
export function atividadeAPartirDe(
  janelaAtiva: boolean,
  segundosNoSistema: number | null,
  minutosAteAusente: number,
): Atividade {
  if (janelaAtiva) return 'no-orbind'
  if (segundosNoSistema === null) return 'parado'
  return segundosNoSistema < minutosAteAusente * 60 ? 'no-computador' : 'parado'
}

/**
 * O que ESTA sessão reporta, juntando a janela do Orbind e o sistema
 * (SPEC-20261020-2341-programa-em-uso-na-presenca, ajuste de 2026-10-28: "ainda não aparece embaixo do
 * nome em qual aplicativo o usuário está ativo").
 *
 * Antes a sessão só saía de "no Orbind" depois do tempo de ociosidade do
 * Console (até 60 min sem tocar na janela) — quem passava a manhã no Excel
 * continuava "no Orbind" e o programa nunca aparecia. Agora, no desktop, a
 * janela SEM FOCO já pergunta ao sistema: com outro programa à frente, é "no
 * computador" com o nome dele; sem programa conhecido (o próprio Orbind à
 * frente, numa aplicação aberta dentro dele, ou shell antigo), continua "no
 * Orbind" até o tempo de ociosidade valer.
 */
export function atividadeDaSessao(
  janelaParada: boolean,
  janelaSemFoco: boolean,
  doSistema: { atividade: Atividade; programa: string | null },
): { atividade: Atividade; programa: string | null } {
  if (!janelaParada && !janelaSemFoco) return { atividade: 'no-orbind', programa: null }
  if (!janelaParada && doSistema.programa === null) return { atividade: 'no-orbind', programa: null }
  return doSistema
}
